<template>
  <page-view
    :is-loading="loadings.drmInfo || loadings.watermarkGroups"
    fill-height
  >
    <template #header>
      <base-header :title="$t('message.pages.funnel.titleIntern')" class="mb-2">
        <template #actions>
          <base-btn v-if="!isMobileBreakpoint" @click="createFunnel">
            <span class="responsive-text">
              {{ $t('message.pages.funnel.emptyState.button') }}
            </span>
          </base-btn>
        </template>
      </base-header>
    </template>

    <template #content>
      <v-col
        v-if="funnels.length > 0"
        :cols="isMobileBreakpoint || isTabletBreakpoint ? 12 : 'auto'"
        :order="isMobileBreakpoint || isTabletBreakpoint ? 'first' : ''"
        class="ml-auto d-flex justify-space-between search-bar"
      >
        <v-input
          v-model="searchQuery"
          style="max-width: 370px"
          prepend-inner-icon="mdi-magnify"
          size="small"
          :placeholder="$t('message.pages.funnel.search')"
          hide-details
          dense
          solo
          flat
        >
          <template v-slot:prepend-inner>
            <v-icon class="neutral-60--text" size="16"> panda-search </v-icon>
          </template>
        </v-input>
        <span
          v-if="!isMobileBreakpoint"
          class="body-regular-smaller textType6--text"
        >
          {{
            funnels.length > 1
              ? `${funnels.length} items`
              : `${funnels.length} item`
          }}
        </span>
      </v-col>
      <v-row
        no-gutters
        :style="
          funnels.length === 0
            ? {
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }
            : {}
        "
      >
        <v-col cols="12">
          <v-row no-gutters>
            <v-col class="pa-0" cols="12">
              <v-row no-gutters class="justify-space-between align-center">
                <v-col cols="12" v-if="!selectedFunnel.id">
                  <template
                    v-if="loadings.watermarkGroups || errors.groupsError"
                  >
                    <v-card
                      class="d-flex flex-row justify-center align-center"
                      width="100%"
                      flat
                    >
                      <loading-logo :value="200" />
                    </v-card>
                  </template>

                  <template v-else>
                    <div class="table-container">
                      <div :style="{ minWidth: dynamicMinWidth }">
                        <v-card
                          v-if="!isFetching && funnels.length > 0"
                          flat
                          height="40px"
                          class="d-flex mb-2 mt-1"
                        >
                          <v-row no-gutters height="100%">
                            <v-card
                              class="d-flex align-center justify-center ml-1"
                              flat
                              :width="columnWidths.selectCommon"
                            >
                              <v-simple-checkbox
                                v-if="hasEditDrmPerm"
                                :color="!anyGroupSelected ? 'primary' : ''"
                                :indeterminate="anyGroupSelected"
                                :ripple="false"
                                :value="anyGroupSelected"
                                @input="selectAllGroups"
                              />
                            </v-card>
                            <v-card
                              class="d-flex align-center justify-start"
                              :width="columnWidths.nameCommon"
                              flat
                            >
                              <v-hover v-slot="{ hover }">
                                <div
                                  class="d-flex align-center justify-start"
                                  @click="switchGroupNameOrder"
                                >
                                  <span
                                    class="caption mt-1 ml-1"
                                    style="height: 24px"
                                  >
                                    <b>{{
                                      $t('message.pages.drm.tableColumns.name')
                                    }}</b>
                                  </span>
                                  <v-icon
                                    v-if="hover && !groupNameOrder"
                                    color="grey lighten-1"
                                    >mdi-menu-down</v-icon
                                  >
                                  <v-icon
                                    v-if="hover && groupNameOrder === 'desc'"
                                    >mdi-menu-down</v-icon
                                  >
                                  <v-icon
                                    v-if="hover && groupNameOrder === 'asc'"
                                    >mdi-menu-up</v-icon
                                  >
                                </div>
                              </v-hover>
                            </v-card>
                            <v-card
                              v-if="columns[1].visible"
                              class="d-flex align-center"
                              height="45px"
                              flat
                              :width="columnWidths.videosCountCommon || '142px'"
                            >
                              <v-hover v-slot="{ hover }">
                                <v-row
                                  no-gutters
                                  align="center"
                                  justify="end"
                                  class="video-header-row"
                                  @click="switchVideoAmountOrder"
                                >
                                  <span
                                    class="caption mt-1"
                                    :class="{ 'mr-2': !columns[0].visible }"
                                    style="height: 24px"
                                  >
                                    <b>{{
                                      $t(
                                        'message.pages.drm.tableColumns.videos'
                                      )
                                    }}</b>
                                  </span>
                                  <v-icon
                                    v-show="hover && !groupVideosOrder"
                                    color="grey lighten-1"
                                    style="margin-left: 4px"
                                  >
                                    mdi-menu-down
                                  </v-icon>
                                  <v-icon
                                    v-show="
                                      hover && groupVideosOrder === 'desc'
                                    "
                                    style="margin-left: 4px"
                                  >
                                    mdi-menu-down
                                  </v-icon>
                                  <v-icon
                                    v-show="hover && groupVideosOrder === 'asc'"
                                    style="margin-left: 4px"
                                  >
                                    mdi-menu-up
                                  </v-icon>
                                  <v-icon
                                    v-show="!hover"
                                    style="visibility: hidden; margin-left: 4px"
                                  >
                                    mdi-menu-down
                                  </v-icon>
                                </v-row>
                              </v-hover>
                            </v-card>
                            <v-card
                              v-if="columns[1].visible"
                              class="d-flex align-center"
                              height="45px"
                              flat
                              :width="'185px'"
                            >
                              <v-hover v-slot="{ hover }">
                                <v-row
                                  no-gutters
                                  align="center"
                                  justify="end"
                                  class="video-header-row"
                                  @click="switchLastModifiedOrder"
                                >
                                  <span
                                    class="caption mt-1"
                                    :class="{ 'mr-2': !columns[0].visible }"
                                    style="height: 24px"
                                  >
                                    <b>{{
                                      $t('message.playlist.table.modification')
                                    }}</b>
                                  </span>
                                  <v-icon
                                    v-show="hover && !lastModifiedOrder"
                                    color="grey lighten-1"
                                    style="margin-left: 4px"
                                  >
                                    mdi-menu-down
                                  </v-icon>
                                  <v-icon
                                    v-show="
                                      hover && lastModifiedOrder === 'desc'
                                    "
                                    style="margin-left: 4px"
                                  >
                                    mdi-menu-down
                                  </v-icon>
                                  <v-icon
                                    v-show="
                                      hover && lastModifiedOrder === 'asc'
                                    "
                                    style="margin-left: 4px"
                                  >
                                    mdi-menu-up
                                  </v-icon>
                                  <v-icon
                                    v-show="!hover"
                                    style="visibility: hidden; margin-left: 4px"
                                  >
                                    mdi-menu-down
                                  </v-icon>
                                </v-row>
                              </v-hover>
                            </v-card>
                          </v-row>
                        </v-card>
                        <v-divider v-if="funnels.length > 0" />
                        <vue-virtual-table
                          ref="virtualTable"
                          v-if="funnels.length > 0 && filteredGroups.length > 0"
                          class="data-table__content"
                          :class="{
                            virtualTable: $vuetify.theme.dark,
                          }"
                          :style="selectedFunnel.id ? 'display: none' : ''"
                          :data="filteredGroups"
                          :config="headers"
                          :height="tableHeight"
                          :itemHeight="48"
                          :minWidth="300"
                          @contextmenu="showDrmMenu"
                          @click="handleSelectGroup"
                        >
                          <template slot-scope="scope" slot="selectCommon">
                            <v-row
                              no-gutters
                              justify="center"
                              align="center"
                              style="height: 100%"
                              class="group-checkbox"
                              :class="{
                                'rowIsSelected-light':
                                  checkIfSelectedGroup(scope.row) &&
                                  $vuetify.theme.dark,
                                'rowIsSelected-dark':
                                  checkIfSelectedGroup(scope.row) &&
                                  $vuetify.theme.dark,
                              }"
                            >
                              <v-simple-checkbox
                                color="primary"
                                :ripple="false"
                                :value="checkIfSelectedGroup(scope.row)"
                                @input="checkboxSelectionGroup(scope.row)"
                              ></v-simple-checkbox>
                            </v-row>
                          </template>
                          <template slot-scope="scope" slot="nameCommon">
                            <v-row
                              no-gutters
                              @click="handleSelectGroup(scope.row)"
                              class="pt-2"
                              style="max-width: 100%"
                              align="center"
                            >
                              <v-col
                                class="d-flex align-center"
                                cols="12"
                                style="min-width: 0"
                              >
                                <div style="width: 16px; height: 16px">
                                  <simple-svg :src="Funel" width="16px" />
                                </div>
                                <span
                                  :title="scope.row.name"
                                  class="text-truncate ml-3"
                                  style="
                                    display: block;
                                    white-space: nowrap;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                  "
                                >
                                  {{ scope.row.name }}
                                </span>
                              </v-col>
                            </v-row>
                          </template>
                          <template slot-scope="scope" slot="videosCountCommom">
                            <v-row
                              no-gutters
                              justify="end"
                              @click="handleSelectGroup(scope.row)"
                            >
                              <span
                                class="text-right"
                                :class="columns[0].visible ? 'mr-8' : 'mr-7'"
                              >
                                {{ scope.row.videos_count }}
                              </span>
                            </v-row>
                          </template>
                          <template
                            slot-scope="scope"
                            slot="modificationCommom"
                          >
                            <div
                              style="width: 100%"
                              class="text-right"
                              @click="handleSelectGroup(scope.row)"
                            >
                              <span class="text-center">
                                {{
                                  new Date(scope.row.created_at).toLocaleString(
                                    'pt-BR',
                                    {
                                      day: '2-digit',
                                    }
                                  ) +
                                  ' ' +
                                  new Date(scope.row.created_at)
                                    .toLocaleString(getLocale(), {
                                      month: 'short',
                                    })
                                    .charAt(0)
                                    .toUpperCase() +
                                  new Date(scope.row.created_at)
                                    .toLocaleString(getLocale(), {
                                      month: 'short',
                                    })
                                    .slice(1) +
                                  ' ' +
                                  new Date(scope.row.created_at).toLocaleString(
                                    'pt-BR',
                                    {
                                      year: 'numeric',
                                    }
                                  )
                                }}
                              </span>
                            </div>
                          </template>

                          <template slot-scope="scope" slot="dotsCommon">
                            <v-row
                              no-gutters
                              class="pl-1"
                              @click.stop="showDrmMenu(scope.row, $event)"
                              style="cursor: pointer"
                            >
                              <span
                                class="dotsRow"
                                :class="{
                                  dotsActive:
                                    showMenuDrm &&
                                    menuItemDrm.id === scope.row.id,
                                }"
                              >
                                <span>
                                  <v-icon
                                    size="16"
                                    :class="
                                      showMenuDrm &&
                                      menuItemDrm.id === scope.row.id
                                        ? 'neutral-20-dark--text'
                                        : 'neutral-40-dark--text'
                                    "
                                  >
                                    mdi-dots-vertical
                                  </v-icon>
                                </span>
                              </span>
                            </v-row>
                          </template>
                        </vue-virtual-table>
                        <template v-else>
                          <v-container
                            fill-height
                            class="mb-10 empty-state-container"
                          >
                            <v-row
                              class="d-flex flex-column"
                              no-gutters
                              justify="center"
                              align="center"
                            >
                              <img
                                :src="
                                  require('@/assets/funnel-empty-state.png')
                                "
                                :width="isMobileBreakpoint ? '96px' : '240px'"
                                class="mb-5"
                              />
                              <div class="empty-state-group">
                                <span
                                  class="body-regular-large neutral-20-dark--text text-center mb-2"
                                >
                                  {{
                                    funnels.length > 0
                                      ? $t(
                                          'message.pages.drm.drmListKeys.notFoundSearchTitle'
                                        )
                                      : $t(
                                          'message.pages.funnel.emptyState.title'
                                        )
                                  }}
                                </span>
                                <span
                                  class="body-regular-small neutral-40-light--text text-center"
                                >
                                  {{
                                    funnels.length > 0
                                      ? $t(
                                          'message.pages.funnel.emptyState.notFoundSearchDescription'
                                        )
                                      : $t(
                                          'message.pages.funnel.emptyState.description'
                                        )
                                  }}
                                </span>
                              </div>
                              <base-btn
                                v-if="
                                  !isMobileBreakpoint && funnels.length === 0
                                "
                                @click="createFunnel"
                                size="small"
                              >
                                <span class="responsive-text">
                                  {{
                                    $t('message.pages.funnel.emptyState.button')
                                  }}
                                </span>
                              </base-btn>
                            </v-row>
                          </v-container>
                        </template>
                      </div>
                    </div>
                  </template>
                  <v-row
                    v-if="errors.groupsError"
                    no-gutters
                    align="center"
                    justify="center"
                  >
                    <v-card width="100%" height="400" class="mb-16" flat>
                      <v-row no-gutters justify="center" align="center">
                        <v-col
                          cols="6"
                          class="d-flex justify-start align-start flex-column"
                        >
                          <span class="text-h6"
                            >{{ $t('message.videoApiError') }}
                            {{ errors.groupsUnknownErrorCode }}).</span
                          >
                          <br />
                          <span class="text-subheading">
                            {{ $t('message.videoApiErrMsg') }} <br />
                            <button
                              type="button"
                              class="primary--text"
                              @click="handleStatusPage"
                            >
                              <span class="primary--text">
                                {{ $t('message.checkStatusPageLink') }}
                              </span>
                            </button>
                          </span>
                          <br />
                        </v-col>
                        <v-col cols="12">
                          <v-row no-gutters align="center" justify="center">
                            <v-btn
                              color="primary"
                              class="ma-2 ml-3 white--text"
                              rounded
                              @click="getAllFunnels"
                            >
                              <span class="mx-2">{{
                                $t('message.tryAgain')
                              }}</span>
                              <v-icon right dark> mdi-cached </v-icon>
                            </v-btn>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-menu
        v-model="showMenuDrm"
        min-width="300px"
        :position-x="x"
        :position-y="y"
        absolute
        offset-y
        :transition="null"
        style="left: 1735px !important"
      >
        <v-list dense>
          <v-tooltip bottom :disabled="menuItemDrm.videos_count > 0">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-list-item
                  :disabled="menuItemDrm.videos_count < 1"
                  @click="handleShare(menuItemDrm)"
                >
                  <span style="height: 100%">
                    {{ $t('message.pages.funnel.menuOptions.share') }}</span
                  >
                </v-list-item>
              </div>
            </template>
            <span>{{ $t('message.pages.funnel.toasts.notShowEmbed') }}</span>
          </v-tooltip>
          <v-tooltip bottom :disabled="menuItemDrm.videos_count > 0">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-list-item
                  :disabled="menuItemDrm.videos_count < 1"
                  @click="handlePreview(menuItemDrm)"
                >
                  <span style="height: 100%">
                    {{ $t('message.pages.funnel.menuOptions.view') }}
                  </span>
                </v-list-item>
              </div>
            </template>
            <span>{{ $t('message.pages.funnel.toasts.notShowView') }}</span>
          </v-tooltip>
          <v-divider></v-divider>
          <v-list-item @click="handleSelectGroup(menuItemDrm)">
            {{ $t('message.edit') }}
          </v-list-item>
          <v-list-item @click="handleEditGroup(menuItemDrm)">
            {{ $t('message.rename') }}
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="handleDeleteGroup(menuItemDrm)">
            <span style="height: 100%; color: #f44336">
              {{ $t('message.remove') }}
            </span>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-row class="data-table__actions d-flex" no-gutters>
        <div>
          <v-col
            v-if="hasEditDrmPerm"
            v-show="showFloatMenu"
            :cols="isMobileBreakpoint || isTabletBreakpoint ? 12 : 'auto'"
          >
            <base-float-menu
              :minWidth="
                isMobileBreakpoint || isTabletBreakpoint ? '380px' : 'auto'
              "
            >
              <span
                class="body-bold-smaller neutral-20-dark--text mr-1"
                style="text-transform: lowercase"
              >
                {{ selectedItemsMessage }}
              </span>
              <div style="border-left: 1px solid #d1d1d1">
                <template v-if="isMobileBreakpoint || isTabletBreakpoint">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <base-btn
                        class="ml-4"
                        size="small"
                        :disabled="!selectedGroups.length"
                        v-bind="attrs"
                        v-on="on"
                        @click="openActionsModal"
                      >
                        {{ $t('message.floatActions') }}
                      </base-btn>
                    </template>
                  </v-menu>

                  <v-dialog v-model="showActionsModal" fullscreen persistent>
                    <v-card>
                      <v-toolbar dense flat>
                        <v-toolbar-title class="ml-3">{{
                          $t('message.floatActions')
                        }}</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon @click="showActionsModal = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-toolbar>

                      <v-card-text class="pa-0">
                        <p v-if="selectedGroups.length > 1" class="px-7">
                          {{ selectedGroups.length }}
                          {{ $t('message.selectedItems') }}
                        </p>
                        <p v-else class="px-7">
                          {{ menuItemDrm.name }}
                          {{ selectedGroups.length === 0 ? 'BETA' : '' }}
                        </p>
                        <v-list class="mt-12">
                          <v-list-item
                            class="px-7"
                            :disabled="menuItemDrm.videos_count < 1"
                            @click="handleShare(menuItemDrm)"
                          >
                            <span style="height: 100%">
                              {{
                                $t('message.pages.funnel.menuOptions.share')
                              }}</span
                            >
                          </v-list-item>
                          <v-list-item
                            class="px-7"
                            :disabled="menuItemDrm.videos_count < 1"
                            @click="handlePreview(menuItemDrm)"
                          >
                            <span style="height: 100%">
                              {{ $t('message.pages.funnel.menuOptions.view') }}
                            </span>
                          </v-list-item>
                          <v-divider></v-divider>
                          <v-list-item
                            v-if="selectedGroups.length === 1"
                            @click="handleSelectGroup(menuItemDrm)"
                            link
                            class="px-7"
                          >
                            <v-list-item-content
                              @click="handlePreview(menuItemDrm)"
                              >{{ $t('message.edit') }}</v-list-item-content
                            >
                          </v-list-item>

                          <v-list-item
                            v-if="selectedGroups.length === 1"
                            @click="handleEditGroup(menuItemDrm)"
                            link
                            class="px-7"
                          >
                            <v-list-item-content>{{
                              $t('message.rename')
                            }}</v-list-item-content>
                          </v-list-item>

                          <v-divider></v-divider>

                          <v-list-item class="px-7" @click="handleDeleteClick">
                            <span style="height: 100%; color: #f44336">
                              {{ $t('message.remove') }}
                            </span>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </template>

                <template v-else>
                  <base-btn
                    class="ml-5"
                    size="small"
                    type="danger"
                    @click="handleDeleteClick"
                  >
                    {{ $t('message.remove') }}
                  </base-btn>
                </template>
              </div>
            </base-float-menu>
          </v-col>
        </div>
      </v-row>

      <div v-if="isMobileBreakpoint" class="card-actions">
        <v-divider></v-divider>
        <div class="actions">
          <base-btn @click="createFunnel" style="width: 100%">
            <span class="responsive-text">
              {{ $t('message.pages.funnel.emptyState.button') }}
            </span>
          </base-btn>
        </div>
      </div>
      <create-funnel />
      <remove-funnel />
      <preview-funnel />
      <rename-funnel />
      <first-modal />
      <share-funnel />
    </template>
  </page-view>
</template>

<script>
// external imports
import { mapMutations, mapState } from 'vuex'
import VueVirtualTable from 'vue-virtual-table'
import { debounce } from 'lodash'

// internal imports
import i18n from '@/translations/lang'
import {
  PANDA_STATUS_PAGE_BR,
  DRM_LINK_PT_BR,
  DRM_LINK_EN_US,
} from '@/config/envs'

// API
import funnelApi from '@/services/funnelApi'
import foldersApi from '@/services/foldersApi'
import Funel from '@/assets/Funel.svg'
// Components
import LoadingLogo from '@/components/LoadingLogo.vue'
import PageView from '@/layout/PageViewV2.vue'
import BaseFloatMenu from '@/components/BaseFloatMenu.vue'
import CreateFunnel from './Modals/CreateFunnel.vue'
import RemoveFunnel from './Modals/RemoveFunnel.vue'
import PreviewFunnel from './Modals/PreviewFunnel.vue'
import RenameFunnel from './Modals/RenameFunnel.vue'
import FirstModal from './Modals/FirstModal.vue'
import ShareFunnel from './Modals/ShareFunnel.vue'
export default {
  name: 'Funnel',
  components: {
    VueVirtualTable,
    LoadingLogo,
    PageView,
    BaseFloatMenu,
    FirstModal,
    BaseHeader: () => import('@/components/BaseHeaderV2.vue'),
    CreateFunnel,
    RemoveFunnel,
    PreviewFunnel,
    RenameFunnel,
    ShareFunnel,
  },
  data: () => ({
    loadings: {
      drmInfo: false,
      activateDrm: false,
      deactivateDrm: false,
      watermarkGroups: false,
    },
    Funel,
    showMenuEmbed: false,
    errors: {
      groupsError: false,
      groupsUnknownErrorCode: 503,
    },
    funnels: [],
    selectedFunnel: {},
    columns: [
      {
        name: '',
        visible: true,
      },
      {
        name: '',
        visible: true,
      },
      {
        name: '',
        visible: true,
      },
    ],
    tableHeight: window.innerHeight - 240,
    isFetching: false,
    db: null,
    selectedGroups: [],
    showFloatMenu: false,
    showActionsModal: false,
    showMenuDrm: false,
    menuItemDrm: {},
    x: 0,
    y: 0,
    groupNameOrder: 'asc',
    groupVideosOrder: 'asc',
    protectionLevelOrder: 'asc',
    lastModifiedOrder: 'asc',
    searchQuery: '',
    crispObserver: null,
    columnWidths: {},
    resizeObserver: null,
  }),
  computed: {
    ...mapState({
      mainFolders: (state) => state.user.mainFolders,
    }),
    ...mapState('user', {
      isTabletBreakpoint: 'isTabletBreakpoint',
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),

    isDarkTheme() {
      return this.$vuetify.theme.dark
    },
    menu() {
      return this.$store.state.user.securityNavItems
    },
    emptyStateTitle() {
      if (this.funnels.length === 0) {
        return this.$t('message.pages.drm.emptyState.title')
      }
      if (this.searchQuery && this.filteredGroups.length === 0) {
        return this.$t('message.pages.drm.searchBar.noResultsTitle')
      }
      return ''
    },
    emptyStateDescription() {
      if (this.funnels.length === 0) {
        return this.$t('message.pages.drm.emptyState.subtitle')
      }
      if (this.searchQuery && this.filteredGroups.length === 0) {
        return this.$t('message.pages.drm.searchBar.noResultsDescription')
      }
      return ''
    },
    selectedItemsMessage() {
      const count = this.selectedGroups.length
      const message =
        count === 1
          ? this.$t('message.selectedItem')
          : this.$t('message.selectedItems')

      return `${count} ${message}`
    },

    hasIncompleteIntegration() {
      return (group) => {
        const source = group.webhook_source_name
        const isHotmartOrEduzz = ['hotmart', 'eduzz'].includes(source)
        return isHotmartOrEduzz && !group.token_verification
      }
    },
    hasSomeIncompleteIntegration() {
      return this.selectedGroups.some((group) =>
        this.hasIncompleteIntegration(group)
      )
    },
    isActivateBtnDisabled() {
      return (
        !this.hasSelection ||
        !this.canActivateAll ||
        this.hasSomeIncompleteIntegration
      )
    },
    activateTooltipMessage() {
      const selected = this.selectedGroups || []
      const total = selected.length

      const hasActive = selected.some((g) => g.active)
      const hasInactive = selected.some((g) => !g.active)

      if (hasActive && hasInactive) {
        return this.$t('message.pages.drm.mixedGroupTooltip')
      }

      if (!hasInactive && hasActive) {
        return this.$t(
          total === 1
            ? 'message.pages.drm.alreadyActiveTooltip'
            : 'message.pages.drm.alreadyActiveTooltipPlural'
        )
      }

      if (this.hasSomeIncompleteIntegration) {
        return this.$t('message.pages.drm.disabledGroupTooltip')
      }

      return ''
    },
    deactivateTooltipMessage() {
      const selected = this.selectedGroups || []
      const total = selected.length

      const hasActive = selected.some((g) => g.active)
      const hasInactive = selected.some((g) => !g.active)

      if (hasActive && hasInactive) {
        return this.$t('message.pages.drm.mixedGroupTooltip')
      }

      if (!hasActive && hasInactive) {
        return this.$t(
          total === 1
            ? 'message.pages.drm.alreadyInactiveTooltip'
            : 'message.pages.drm.alreadyInactiveTooltipPlural'
        )
      }

      return ''
    },
    hasSelection() {
      return this.selectedGroups.length > 0
    },
    filteredGroups() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) {
        return this.funnels
      }
      return this.funnels.filter((item) =>
        item.name.toLowerCase().includes(query)
      )
    },
    hasEditDrmPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-watermark') ??
        false
      )
    },
    isLocalePT_BR() {
      return this.$i18next.resolvedLanguage === 'pt'
    },
    drmLink() {
      return this.isLocalePT_BR ? DRM_LINK_PT_BR : DRM_LINK_EN_US
    },
    anyGroupSelected() {
      return this.selectedGroups.length > 0
    },
    getQuantityInsideGroup() {
      return (group) => {
        const directVideos = parseInt(group?.video_ids?.length || 0)
        const videosInFolders = this.getVideosCountInsideFolders(
          group.folder_ids
        )
        const total = directVideos + videosInFolders

        const messageKey = `message.pages.drm.tableRow.${
          total === 1 ? 'video' : 'videos'
        }`
        return this.$t(messageKey, { count: total })
      }
    },
    getProtectionType() {
      return (security_type) => {
        return this.$t(
          `message.pages.drm.tableRow.${security_type || 'regular'}`
        )
      }
    },
    searchList() {
      return []
    },
    headers() {
      const headers = [
        {
          prop: '_action',
          name: 'action',
          actionName: 'selectCommon',
          width: 42,
          alignItems: 'start',
        },
        {
          prop: '_action',
          name: i18n.t('message.name'),
          actionName: 'nameCommon',
          alignItems: 'start',
        },
        {
          prop: '_action',
          actionName: 'videosCountCommom',
          alignItems: 'right',
          width: 150,
          isHidden: !this.columns[1].visible,
        },
        {
          prop: '_action',
          actionName: 'modificationCommom',
          alignItems: 'right',
          width: 150,
          isHidden: !this.columns[2].visible,
        },
        {
          prop: '_action',
          actionName: 'dotsCommon',
          name: '',
          width: 50,
          alignItems: 'center',
        },
      ]

      return headers
    },
  },
  async created() {
    this.isFetching = true
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
    if (!this.mainFolders.length) {
      this.readUserFolders()
    }
    await this.getAllFunnels()
    this.isFetching = false
    this.initColumnResizeSync()
  },
  mounted() {
    this.$root.$on('reloadFunnels', async () => {
      this.isFetching = true
      await this.getAllFunnels()
      this.selectedGroups = []
      this.isFetching = false
    })
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    this.handleWidth()
    window.addEventListener('resize', this.handleWidth)
    this.watchCrispAndAdjust()
    setTimeout(() => {
      if (!localStorage.getItem('openFirstModal') && !this.funnels.length)
        this.$root.$emit('openFirstModal')
      localStorage.setItem('openFirstModal', true)
    }, 3000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWidth)
    window.removeEventListener('resize', this.handleResize)
    this.adjustCrispStyles(true)
    this.disconnectCrispObserver()
    this.$root.$off('navigateToKeys')
    this.$root.$off('navigateToGroup')
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  watch: {
    selectedGroups: function () {
      this.showFloatMenu = this.anyGroupSelected
    },
    itemNameOrder: function () {
      this.orderItemsByName()
    },
    groupNameOrder: function () {
      this.orderGroupByName()
    },
    groupVideosOrder: function () {
      this.orderGroupByVideos()
    },
    lastModifiedOrder: function () {
      this.orderByLastModified()
    },
    protectionLevelOrder: function () {
      this.orderByProtectionLevel()
    },
    isMobileBreakpoint: function () {
      this.adjustCrispStyles()
    },
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_MAIN_FOLDERS',
      'UPDATE_SHOULD_FETCH_ALL_VIDEOS',
      'UPDATE_DELETED_FOLDERS',
      'UPDATE_ALL_FOLDERS',
    ]),
    handleShare(funnel) {
      this.$root.$emit('openShareFunnel', funnel)
    },
    handlePreview(funnel) {
      this.$root.$emit('openPreviewFunnel', funnel)
    },
    createFunnel() {
      this.$root.$emit('openCreateFunnel')
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    collectColumnWidths(retries = 10) {
      const table = this.$refs.virtualTable?.$el
      if (!table) return

      const firstRow = table.querySelector('.item-line')
      if (firstRow) {
        const cells = firstRow.querySelectorAll('.item-cell')
        const widths = {}

        cells.forEach((cell, index) => {
          const width = cell.getBoundingClientRect().width
          const actionName = this.headers[index]?.actionName || `col_${index}`
          widths[actionName] = Math.round(width)
        })

        this.columnWidths = widths
      } else if (retries > 0) {
        setTimeout(() => this.collectColumnWidths(retries - 1), 100)
      }
    },
    initColumnResizeSync() {
      this.$nextTick(() => {
        this.collectColumnWidths()

        const table = this.$refs.virtualTable?.$el
        if (table) {
          this.resizeObserver = new ResizeObserver(() => {
            this.collectColumnWidths()
          })

          this.resizeObserver.observe(table)
        }
      })
    },
    orderByProtectionLevel() {
      if (this.protectionLevelOrder === 'asc') {
        this.funnels.sort((a, b) =>
          this.getProtectionType(a.security_type).localeCompare(
            this.getProtectionType(b.security_type)
          )
        )
      }
      if (this.protectionLevelOrder === 'desc') {
        this.funnels.sort(
          (a, b) =>
            -this.getProtectionType(a.security_type).localeCompare(
              this.getProtectionType(b.security_type)
            )
        )
      }
    },
    switchProtectionLevelOrder() {
      if (!this.protectionLevelOrder || this.protectionLevelOrder === 'desc')
        return (this.protectionLevelOrder = 'asc')
      if (this.protectionLevelOrder === 'asc')
        return (this.protectionLevelOrder = 'desc')
    },
    orderGroupByVideos() {
      if (this.groupVideosOrder === 'asc') {
        this.funnels.sort((a, b) => a.videos_count - b.videos_count)
      }
      if (this.groupVideosOrder === 'desc') {
        this.funnels.sort((a, b) => b.videos_count - a.videos_count)
      }
    },
    orderByLastModified() {
      if (this.lastModifiedOrder === 'asc') {
        this.funnels.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        )
      }
      if (this.lastModifiedOrder === 'desc') {
        this.funnels.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      }
    },
    switchLastModifiedOrder() {
      if (!this.lastModifiedOrder || this.lastModifiedOrder === 'desc')
        return (this.lastModifiedOrder = 'asc')
      if (this.lastModifiedOrder === 'asc')
        return (this.lastModifiedOrder = 'desc')
    },
    switchVideoAmountOrder() {
      if (!this.groupVideosOrder || this.groupVideosOrder === 'desc')
        return (this.groupVideosOrder = 'asc')
      if (this.groupVideosOrder === 'asc')
        return (this.groupVideosOrder = 'desc')
    },
    orderGroupByName() {
      if (this.groupNameOrder === 'asc') {
        this.funnels.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
      if (this.groupNameOrder === 'desc') {
        this.funnels.sort(
          (a, b) => -a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
    },
    switchGroupNameOrder() {
      if (!this.groupNameOrder || this.groupNameOrder === 'desc')
        return (this.groupNameOrder = 'asc')
      if (this.groupNameOrder === 'asc') return (this.groupNameOrder = 'desc')
    },
    orderItemsByName() {
      if (this.itemNameOrder === 'asc') {
        this.loadedVideos.sort((a, b) => a.title.localeCompare(b.title))
        this.loadedFolders.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
      if (this.itemNameOrder === 'desc') {
        this.loadedVideos.sort((a, b) => -a.title.localeCompare(b.title))
        this.loadedFolders.sort(
          (a, b) => -a.name.localeCompare(b.name, undefined, { numeric: true })
        )
      }
    },
    async readUserFolders() {
      try {
        let ret = await foldersApi.getUserFolders()
        let notDeletedFolders = ret.data.folders.filter(
          (folder) => folder.status != false
        )
        let deletedFolders = ret.data.folders.filter(
          (folder) => folder.status === false
        )
        this.UPDATE_MAIN_FOLDERS(notDeletedFolders)
        this.UPDATE_DELETED_FOLDERS(deletedFolders)
        this.UPDATE_ALL_FOLDERS(ret.data.folders)
      } catch (e) {
        console.error(e)
      }
    },
    openActionsModal() {
      if (this.selectedGroups.length === 1) {
        this.menuItemDrm = this.selectedGroups[0]
      }
      this.showActionsModal = true
    },
    showDrmMenu(item, e) {
      e.preventDefault()

      this.menuItemDrm = item
      this.selectedGroups = [item]
      this.showMenuDrm = false
      this.x = e.clientX - 286
      this.y = e.clientY + 17
      setTimeout(() => {
        if (this.isMobileBreakpoint) {
          this.showActionsModal = true
        } else {
          this.showMenuDrm = true
        }
      }, 50)
    },
    checkIfSelectedGroup(item) {
      let foundItem = this.selectedGroups.find((searchedItem) => {
        return searchedItem.id === item.id
      })
      if (foundItem) return true
      return false
    },
    checkboxSelectionGroup(item) {
      let alreadySelected = this.checkIfSelectedGroup(item)
      if (alreadySelected) {
        this.selectedGroups = this.selectedGroups.filter(
          (group) => group.id !== item.id
        )
      } else {
        this.selectedGroups.push(item)
      }
    },
    handleEditGroup(group) {
      const groupSelect = { ...group }
      this.$root.$emit('openRenameFunnel', groupSelect)
    },
    handleActivationClick() {
      this.showMenuDrm = false
      this.showActionsModal = false

      if (this.hasSelection) {
        const selectedCount = this.selectedGroups?.length
        if (selectedCount > 1) {
          this.handleActivationMultipleGroups()
        } else {
          this.handleActivationGroup(this.selectedGroups[0])
        }
      } else {
        this.handleActivationGroup(this.menuItemDrm)
      }
    },
    handleSelectGroup(group) {
      this.selectedFunnel = group
      this.selectedGroups = []
      this.showMenuDrm = false
      this.showActionsModal = false
      if (
        this.$route.name != 'FunnelPage' &&
        this.$route.params.id != group.id
      ) {
        this.$router.push({
          name: 'FunnelPage',
          params: { id: group.id },
        })
      }
    },
    async getAllFunnels() {
      this.loadings.watermarkGroups = true
      this.errors.groupsError = false
      try {
        let ret = await funnelApi.getAllFunnels()
        this.funnels = ret.data.data
      } catch (error) {
        this.loadings.watermarkGroups = false
        this.errors.groupsError = true
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError.message.includes('Network Error')) {
            this.groupsUnknownErrorCode = '504'
            return
          }
        }
        this.errors.groupsUnknownErrorCode = error.code
      }
      this.loadings.watermarkGroups = false
    },

    handleDeleteClick() {
      this.showMenuDrm = false
      this.showActionsModal = false
      if (this.hasSelection) {
        const selectedCount = this.selectedGroups?.length
        if (selectedCount > 1) {
          this.handleDeleteMultipleGroups()
        } else {
          this.handleDeleteGroup(this.selectedGroups[0])
        }
      } else {
        this.handleDeleteGroup(this.menuItemDrm)
      }
    },
    handleDeleteMultipleGroups() {
      this.$root.$emit('openRemoveFunnel', {
        groups: this.selectedGroups,
      })
    },
    async handleDeleteGroup(group) {
      const countVideos = group.videos_count
      this.$root.$emit('openRemoveFunnel', {
        group,
        countVideos,
      })
    },
    selectAllGroups() {
      if (this.selectedGroups.length === this.filteredGroups.length) {
        this.selectedGroups = []
        return
      }
      this.selectedGroups = [...this.filteredGroups]
    },
    getVideosCountInsideFolders(folderIds) {
      if (!folderIds?.length) return 0

      return folderIds.reduce(
        (total, folderId) => total + this.calculateVideoCount(folderId),
        0
      )
    },
    calculateVideoCount(folderId) {
      const allFolders = this.mainFolders

      const parentFolders = allFolders.filter(
        (folder) => folder.id === folderId
      )

      const videosCountInChildFolders = this.countVideosInChildFolders(
        allFolders,
        folderId
      )

      const videoCountInParentFolders = parentFolders
        .map((folder) => parseInt(folder.videos_count, 10) || 0)
        .reduce((acc, count) => acc + count, 0)

      return videosCountInChildFolders + videoCountInParentFolders
    },
    countVideosInChildFolders(allFolders, folderId) {
      const childFoldersIds = []

      // Função recursiva para procurar pastas com parent_folder_id igual a folderId
      function findChildFolders(currentFolderId) {
        const foldersWithParent = allFolders.filter(
          (folder) => folder.parent_folder_id === currentFolderId
        )

        for (let folder of foldersWithParent) {
          childFoldersIds.push(folder.id)
          findChildFolders(folder.id)
        }
      }

      findChildFolders(folderId)

      // Soma os video_count das pastas encontradas
      const totalChildFoldersVideoCount = childFoldersIds.reduce(
        (acc, currentFolderId) => {
          const folder = allFolders.find((f) => f.id === currentFolderId)
          const videoCount = folder ? parseInt(folder.videos_count, 10) ?? 0 : 0
          return acc + videoCount
        },
        0
      )

      return totalChildFoldersVideoCount
    },
    handleStatusPage() {
      return window.open(PANDA_STATUS_PAGE_BR, '_blank')
    },
    handleResize: debounce(function () {
      setTimeout(() => {
        this.setTableHeight()
      }, 50)
    }, 100),
    setTableHeight() {
      const line = document.querySelector('.item-line')
      const lHeight = line?.clientHeight || 48

      const fixedHeights = 48 + 65 + 130
      const windowHeight = window.innerHeight

      const mobileBottomActionsHeight = this.isMobileBreakpoint ? 40 : 0

      const availableHeight =
        windowHeight - fixedHeights - mobileBottomActionsHeight

      const snappedHeight = availableHeight - (availableHeight % lHeight)

      this.tableHeight = snappedHeight
    },
    handleWidth() {
      const windowWidth = window.innerWidth

      this.columns[0].visible = windowWidth >= 1245
      this.columns[1].visible = windowWidth >= 1000
      this.columns[2].visible = windowWidth >= 800
    },
    dynamicMinWidth() {
      const BASE_WIDTH = 300
      const MAX_WIDTH = 770

      const columnWidths = {
        0: 170,
        1: 165,
        2: 135,
      }

      const totalWidth = this.columns.reduce(
        (sum, col, index) =>
          col.visible ? sum + (columnWidths[index] || 0) : sum,
        BASE_WIDTH
      )

      return `${Math.min(totalWidth, MAX_WIDTH)}px`
    },
    watchCrispAndAdjust(reset = false) {
      this.crispObserver = new MutationObserver(() => {
        const crisp = document.querySelector('.cc-1m2mf')
        const button = document.querySelector('.cc-157aw')
        const badge = document.querySelector('.cc-1bvfm')
        if (crisp && button && badge) {
          this.adjustCrispStyles(reset)
          this.crispObserver.disconnect()
          this.crispObserver = null
        }
      })
      this.crispObserver.observe(document.body, {
        childList: true,
        subtree: true,
      })
    },
    disconnectCrispObserver() {
      if (this.crispObserver) {
        this.crispObserver.disconnect()
        this.crispObserver = null
      }
    },
    adjustCrispStyles(reset = false) {
      const crisp = document.querySelector('.cc-1m2mf')
      const button = document.querySelector('.cc-157aw')
      const badge = document.querySelector('.cc-1bvfm')

      const isMobile = this.isMobileBreakpoint
      const shouldReset = reset || !isMobile
      const bottom = shouldReset ? 20 : 80

      if (crisp) {
        crisp.style.setProperty('bottom', `${bottom}px`, 'important')
      }

      if (button) {
        button.style.setProperty(
          'width',
          shouldReset ? '54px' : '40px',
          'important'
        )
        button.style.setProperty(
          'height',
          shouldReset ? '54px' : '40px',
          'important'
        )
      }

      if (badge) {
        badge.style.setProperty(
          'width',
          shouldReset ? '32px' : '26px',
          'important'
        )
        badge.style.setProperty(
          'height',
          shouldReset ? '26px' : '20px',
          'important'
        )
        badge.style.setProperty(
          'top',
          shouldReset ? '16px' : '12px',
          'important'
        )
        badge.style.setProperty(
          'left',
          shouldReset ? '11px' : '7px',
          'important'
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .main-scroll {
  border: none !important;
}

::v-deep .rowSlot {
  color: varFigma(neutral-20-dark);
  font-size: 14px;
}

::v-deep .rowSlot .row {
  padding-top: 0 !important;
  height: 100% !important;
  display: flex;
  align-content: center;
}

::v-deep .v-list {
  padding: 0 !important;
}

::v-deep .v-slide-group__prev.v-slide-group__prev--disabled {
  display: none !important;
}

::v-deep .page-view__content {
  overflow: hidden;
}

::v-deep .group-checkbox {
  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }
}

::v-deep .virtualTable .item-line .item-cell {
  border-bottom: 1px solid var(--v-neutral-95-lightest-base) !important;
}

::v-deep .svg-api svg path {
  fill: varFigma(neutral-60) !important;
}

.table-container {
  overflow-x: auto;
}

.dotsRow {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dotsActive {
  border-radius: var(--spacing-02, 4px);
  background: varFigma(neutral-90);
}

.caption {
  color: varFigma(neutral-20-dark);
}

.card-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  .actions {
    padding: 16px 20px;
    background-color: varFigma(neutral-100-white);
  }
}

.empty-state-container {
  height: 60vh;
}

.empty-state-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.search-bar {
  margin-bottom: 8px;
}
::v-deep .item-line:hover {
  background-color: varFigma(neutral-99) !important;
  cursor: pointer;
}
::v-deep .item-line:has(.rowIsSelected-light) {
  background-color: var(--brand-95-lighter-light) !important;
}

::v-deep .item-line:has(.rowIsSelected-dark) {
  background-color: var(--v-neutral-darken1) !important;
}

@media (max-width: 768px) {
  .empty-state-group {
    span:first-child {
      font-size: 16px;
    }
    span:last-child {
      font-size: 14px;
    }
  }

  .search-bar {
    margin-bottom: 11px;
  }
}
</style>

<style lang="scss">
.pageTitle {
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  color: var(--v-brand-darken2);
}

.svgSize {
  width: 450px;
}

.displayBlock {
  display: block;
}

.displayNone {
  display: none;
}

.t-header {
  display: none;
}

.t-header span {
  font-weight: bold;
}

.toolbarMargin .v-toolbar__content {
  margin-left: 0;
  padding-left: 0;
}

.noFilesAddedText {
  font-weight: 400;
  font-size: 20px;
  line-height: 135%;
  text-align: center;
  color: #d6d6d6;
}

.svgArrowUp {
  position: relative;
  bottom: 300px;
  right: 40px;
}

.pointerTable {
  cursor: pointer;
  overflow: unset !important;
}
</style>
